import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Badge } from "@/shared/ui";
import { safeHttpsUrl } from "../../lib/safeUrl";
import type { PreviewCardProps } from "../../lib/previewCards";
import { CheckIcon } from "../icons";
import {
  clampText,
  formatPreviewDate,
  MAX_PREVIEW_ROWS,
  PreviewEmpty,
  PreviewFooterLink,
  PreviewShowingOf,
} from "./shared";

/**
 * Shaped from observed `find-tasks` / `find-tasks-by-date` structured output
 * of the Todoist MCP server (ai.todoist.net/mcp):
 * `{tasks: [{id, content, description, dueDate?, recurring, priority: "p4",
 * projectId, labels, checked}], nextCursor, totalCount, hasMore, …}`.
 * Spine: a `tasks` array of objects with a string `content`. Everything else
 * is optional and `catch`-tolerant; unknown keys are ignored (zod default).
 */
export const todoistTasksSchema = z.object({
  tasks: z.array(
    z.object({
      content: z.string(),
      dueDate: z.string().optional().catch(undefined),
      priority: z.string().optional().catch(undefined),
      checked: z.boolean().optional().catch(undefined),
      url: z.string().optional().catch(undefined),
    }),
  ),
});

type TodoistTasksData = z.infer<typeof todoistTasksSchema>;

/** Read-only Todoist task list: content, due-date chip, priority marker. */
export function TodoistTasksCard({ data, connectorName }: PreviewCardProps<TodoistTasksData>) {
  const { t, i18n } = useTranslation();
  const shown = data.tasks.slice(0, MAX_PREVIEW_ROWS);
  // First https task URL in the payload, else the Todoist app itself.
  const footerHref =
    data.tasks.map((task) => safeHttpsUrl(task.url)).find((href) => href !== null) ??
    "https://app.todoist.com/";

  return (
    <div className="flex flex-col">
      {shown.length === 0 ? (
        <PreviewEmpty />
      ) : (
        <ul className="flex flex-col">
          {shown.map((task, index) => {
            const priority = task.priority && /^p[1-3]$/.test(task.priority) ? task.priority : null;
            return (
              <li key={index} className="flex items-center gap-2 px-3 py-2">
                {task.checked ? <CheckIcon className="size-3.5 shrink-0 text-ink-muted" /> : null}
                <span
                  className={
                    task.checked
                      ? "min-w-0 flex-1 truncate font-sans text-[13px] text-ink-muted line-through"
                      : "min-w-0 flex-1 truncate font-sans text-[13px] text-ink"
                  }
                >
                  {clampText(task.content)}
                </span>
                {priority ? (
                  <Badge tone="soft" size="sm">
                    {priority.toUpperCase()}
                  </Badge>
                ) : null}
                {task.dueDate ? (
                  <Badge tone="soft" size="sm">
                    {t("chat.preview.due", {
                      date: formatPreviewDate(task.dueDate, i18n.language),
                    })}
                  </Badge>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {data.tasks.length > shown.length ? (
        <PreviewShowingOf shown={shown.length} total={data.tasks.length} />
      ) : null}
      <PreviewFooterLink href={footerHref} connector={connectorName || "Todoist"} />
    </div>
  );
}
